import { useEffect, useRef, useState, type CSSProperties } from "react";
import { authClient } from "../lib/auth";
import { rememberSignedIn } from "../lib/returnTo";
import { Wordmark } from "../components/Wordmark";
import { Avatar } from "../components/ui";
import { SealedStrip, StaticMessage } from "../components/Discussion";
import { Envelope } from "../components/Moments";

/*
 * The signed-out home page (PRD "Pagemates home page", built against the mock
 * in artifact CnK2tF4wUqHVX7PEoCjisd). Prebuilt to plain HTML (prerender.tsx),
 * so it paints before any script or API call; the demo is the only part that
 * needs JavaScript. It is also the router's "/" (after signing out, say), so
 * links are plain <a>: nothing here depends on the router.
 *
 * Words: what happens to the reader, never the app's own names for things
 * (no Talk, sealed, position, shelf as a feature name, admin, PWA...).
 */

const START = "/signin?next=%2Fclubs%2Fnew";
const FIND = "/signin?next=%2Fclubs%2Fbrowse";

/** Signed in: straight to your clubs. An old sign-in link (/?next=…) goes on to the sign-in page with it. */
function useSendOn() {
  useEffect(() => {
    const next = new URLSearchParams(window.location.search).get("next");
    if (next) { window.location.replace(`/signin?next=${encodeURIComponent(next)}`); return; }
    void authClient.getSession().then(({ data }) => {
      rememberSignedIn(Boolean(data));
      if (data) window.location.replace("/clubs");
    }).catch(() => { /* offline: stay on the page */ });
  }, []);
}

export function Home() {
  useSendOn();
  return (
    <div className="home">
      <header className="h-top h-gut">
        <div className="h-in">
          <a href="/" className="h-wm" aria-label="Pagemates home"><Wordmark size="sm" /></a>
          <a href="/signin" className="h-signin">Sign in</a>
        </div>
      </header>

      <main>
        <section className="h-hero h-gut">
          <div className="h-in">
            <div>
              <h1>A book club where nobody spoils the ending.</h1>
              <p className="h-lede">Read at your own pace, see where everyone's up to, and talk about the book without ruining it for anyone behind you.</p>
              <div className="h-ctas">
                <a className="btn primary" href={START}>Start a club</a>
                <a className="btn" href={FIND}>Find a club</a>
              </div>
              <p className="h-note">Free. Sign in with Discord, Google or Apple.</p>
            </div>
            <Demo />
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="how-h">
          <div className="h-in">
            <p className="h-eyebrow">How it works</p>
            <h2 id="how-h">A club takes about a minute to start.</h2>
            <ol className="h-steps">
              <li>Start a club and send your friends a link.</li>
              <li>Everyone reads at their own speed, and you can see where they are.</li>
              <li>Meet up, in person or online, with the questions everyone saved along the way.</li>
            </ol>
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="q-h">
          <div className="h-in">
            <p className="h-eyebrow">What you get</p>
            <h2 id="q-h">The questions every book club asks.</h2>
            <div className="h-feats">
              <Feature q="What if my friends read faster than me?" a="Their comments wait for you. When you catch up, they open. They can even leave you a note at a page, and it's waiting when you get there.">
                <StaticMessage name="Maya" pos="page 60" body="The letters in chapter four change everything." />
                <SealedStrip face={<Avatar name="Theo" />}><span><b>Hidden until page 180</b></span></SealedStrip>
                <div className="h-waiting"><Envelope /><span>Theo left you a note at page 150</span></div>
              </Feature>
              <Feature q="How do we know if we'll finish in time?" a="Everyone's place in the book shows on one line, with a gentle goal for each meeting. Nobody gets nagged.">
                <div className="h-track">
                  <span className="h-goal" style={{ left: "62%" }}><span>Goal for Thursday</span></span>
                  <span className="h-face" style={{ left: "18%" }}><Avatar name="Sam" /></span>
                  <span className="h-face me" style={{ left: "41%" }}><span className="avatar">You</span></span>
                  <span className="h-face" style={{ left: "55%" }}><Avatar name="Maya" /></span>
                  <span className="h-face" style={{ left: "79%" }}><Avatar name="Theo" /></span>
                </div>
                <p className="h-cap">About 20 pages a day gets you there by Thursday.</p>
              </Feature>
              <Feature q="How do we choose the next book?" a="Anyone can suggest one. Then whoever runs the club picks, everyone votes, or you take turns.">
                <div className="h-vote">
                  {([["North of the Lighthouse", "#8a3a24", "#b04e30", 84, 11], ["The Salt Archive", "#3b3352", "#544a75", 60, 8], ["A Year of Small Rooms", "#9a7a2e", "#c29a3e", 30, 4]] as const).map(([t, a, b, w, pts]) => (
                    <div className="h-vrow" key={t}>
                      <Cover a={a} b={b} /><span className="n">{t}</span><span className="bar"><i style={{ width: `${w}%` }} /></span><span className="pts">{pts} points</span>
                    </div>
                  ))}
                </div>
                <p className="h-cap">4 of 5 have voted.</p>
              </Feature>
              <Feature q="What happens at the meeting?" a="Questions people saved while reading become the agenda, and anyone can take notes for the club.">
                <ul className="h-agenda">
                  <li className="done">Was the gardener lying the whole time?</li>
                  <li>Why does the orchard only grow at night?</li>
                  <li>Would you have opened the last letter?</li>
                </ul>
                <p className="h-cap">Thursday, 6:30 PM · 4 going</p>
              </Feature>
              <Feature q="What do we end up with?" a="Every book you've read together, with everyone's ratings and favourite quotes, and the guesses you each made at the start, opened when you finish.">
                {/* The guess stands on the plank with the books, so the drawing holds together at every width. */}
                <div className="h-books h-books-guess">
                  {([["#1f4a52", "#2e6a72", 76], ["#8a3a24", "#b04e30", 84], ["#3b3352", "#544a75", 74], ["#9a7a2e", "#c29a3e", 80]] as const).map(([a, b, h], i) => <Cover key={i} a={a} b={b} style={{ width: 34, height: h }} />)}
                  <div className="paper-note h-guess">
                    <div className="from">Sam's guess · p. 20</div>
                    <p className="txt">The gardener planted it for someone who never came back.</p>
                  </div>
                </div>
                <div className="h-plank"><span className="h-plate">2026</span></div>
                <p className="h-cap h-stars">Club average <span aria-hidden="true">★★★★☆</span></p>
              </Feature>
            </div>
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="find-h">
          <div className="h-in">
            <p className="h-eyebrow">Find a club</p>
            <h2 id="find-h">Or join one that's already reading.</h2>
            <p className="h-sub">Some clubs welcome new members. Others are private, and you join with a link from a friend.</p>
            <div className="h-clubs">
              <ClubCard name="Lantern Readers" about="Slow books, fast opinions." meta="34 members · about 20 pages a day" a="#1f4a52" b="#2e6a72" />
              <ClubCard name="The Long Table" about="One big classic a month, read slowly and together." meta="57 members · relaxed pace" a="#6b2e2e" b="#933f3a" />
              <ClubCard name="Orbit Club" about="Science fiction and the people who love arguing about it." meta="128 members · fast pace" a="#2e3a5c" b="#3f5285" />
            </div>
            <div className="h-row-end"><a className="btn" href={FIND}>Browse clubs</a><span className="h-cap">Example clubs. You'll see real ones after signing in.</span></div>
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="faq-h">
          <div className="h-in">
            <p className="h-eyebrow">Questions</p>
            <h2 id="faq-h">Good to know</h2>
            <div className="h-faq">
              <details><summary>Does it cost anything?</summary><p>No.</p></details>
              <details><summary>Who can see what we write?</summary><p>Only people in your club. Private clubs don't appear anywhere.</p></details>
              <details><summary>What do I need to sign up?</summary><p>A Discord, Google or Apple account. We only use it to sign you in, and we never post anything for you.</p></details>
              <details><summary>Is there an app?</summary><p>It works in any browser. On your phone, add it to your home screen and it behaves like an app.</p></details>
              <details><summary>What's Hardcover?</summary><p>A free site for keeping track of what you read. If you use it, Pagemates can keep your place in the book up to date for you. You don't need it.</p></details>
            </div>
          </div>
        </section>

        <section className="h-closing h-gut">
          <h2>Your next book is better with company.</h2>
          <a className="btn primary" href={START}>Start a club</a>
        </section>
      </main>

      <footer className="h-foot h-gut">
        <div className="h-in"><span>Pagemates</span><a href="/policy.html">Policy and contact</a></div>
      </footer>
    </div>
  );
}

function Feature({ q, a, children }: { q: string; a: string; children: React.ReactNode }) {
  return (
    <div className="h-feat">
      <div><h3>{q}</h3><p>{a}</p></div>
      <div className="h-vig" aria-hidden="true">{children}</div>
    </div>
  );
}

/** A made-up book: two tones, spine and board. */
function Cover({ a, b, style }: { a: string; b: string; style?: CSSProperties }) {
  return <span className="h-cover" aria-hidden="true" style={{ background: `linear-gradient(100deg, ${a} 0 5px, ${b} 5px)`, ...style }} />;
}

function ClubCard({ name, about, meta, a, b }: { name: string; about: string; meta: string; a: string; b: string }) {
  return (
    <div className="h-club">
      <Cover a={a} b={b} />
      <div><b>{name}</b><span className="d">{about}</span><span className="m">{meta}</span></div>
    </div>
  );
}

/* ---------- the demo: a slider for how far you've read over a made-up conversation ---------- */

const BOOK_PAGES = 240;
const START_PAGE = 40;
const SAID = [
  { name: "Sam", page: 12, body: "Ten pages in and I already love the narrator." },
  { name: "Maya", page: 38, body: "The first letter made me go back and reread the opening." },
  { name: "Theo", page: 90, body: "Wait, so the gardener knew all along?" },
  { name: "Priya", page: 160, body: "That night in the orchard is the best chapter so far." },
  { name: "Jun", page: 210, body: "I did not see that last letter coming." },
];
// One note left at a page, for whoever gets there: it waits in an envelope and opens as you pass it.
const NOTE = { name: "Maya", page: 120, body: "Slow down for the next chapter. It's the one we'll talk about all night." };
const visibleAt = (page: number) => SAID.filter((m) => m.page <= page).length;
const messages = (n: number) => `${n} ${n === 1 ? "message" : "messages"}`;

function Demo() {
  const [page, setPage] = useState(START_PAGE);
  // Messages only fade in once someone has moved the slider, not on first paint.
  const [moved, setMoved] = useState(false);
  const [said, setSaid] = useState("");
  const last = useRef(visibleAt(START_PAGE));
  const open = visibleAt(page);
  const noteOpen = page >= NOTE.page;

  const move = (p: number) => {
    setPage(p); setMoved(true);
    const n = visibleAt(p), d = n - last.current;
    const lines = [];
    if (d > 0) lines.push(`${d} more ${d === 1 ? "message" : "messages"} visible`);
    else if (d < 0) lines.push(`${messages(-d)} hidden again`);
    if (p >= NOTE.page && page < NOTE.page) lines.push(`${NOTE.name} left you a note, and it opened`);
    else if (p < NOTE.page && page >= NOTE.page) lines.push("The note is waiting again");
    if (lines.length) setSaid(lines.join(". "));
    last.current = n;
  };

  const before = SAID.filter((m) => m.page < NOTE.page), after = SAID.filter((m) => m.page >= NOTE.page);
  const row = (m: typeof SAID[number]) => m.page <= page
    ? <StaticMessage key={`${m.name}-open`} name={m.name} pos={`page ${m.page}`} body={m.body} className={moved ? "arrive" : undefined} />
    : <SealedStrip key={`${m.name}-hidden`} face={<Avatar name={m.name} />}><span><b>Hidden until page {m.page}</b></span></SealedStrip>;

  return (
    <div className="h-demo" role="group" aria-labelledby="demo-h">
      <div className="h-demo-book">
        <Cover a="#1f4a52" b="#2e6a72" style={{ width: 34, height: 50 }} />
        <div><b id="demo-h">The Glass Orchard</b><span>A made-up book · {BOOK_PAGES} pages · 5 readers</span></div>
      </div>
      <div className="h-msgs">
        {before.map(row)}
        {noteOpen ? (
          <div key="note-open" className={`h-letter${moved ? " opening" : ""}`}>
            {moved && <Envelope />}
            <div className="paper-note">
              <div className="from">{NOTE.name} left this for you at page {NOTE.page}</div>
              <p className="txt">{NOTE.body}</p>
            </div>
          </div>
        ) : (
          <div key="note-wait" className="h-waiting"><Envelope /><span>A note from {NOTE.name} is waiting at page {NOTE.page}</span></div>
        )}
        {after.map(row)}
      </div>
      <div className="h-slider">
        <label htmlFor="demo-page"><span>You've read to <b>page {page}</b></span><span className="muted">{open} of {SAID.length} showing</span></label>
        <input type="range" id="demo-page" min={0} max={BOOK_PAGES} step={1} value={page} aria-valuetext={`page ${page}`} aria-describedby="demo-cap"
          onChange={(e) => move(Number(e.target.value))} />
        <p className="h-demo-cap" id="demo-cap">Drag to see what you'd see. Every comment remembers how far its writer had read. You only see the ones you've caught up to.</p>
        <p className="sr-only" aria-live="polite">{said}</p>
      </div>
    </div>
  );
}
